import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { IGroupMounting } from "../models/groupMounting";
import { IGroupMountingApiResponse } from "../models/api/groupMountingApiResponse";
import { IGroupMountingFilter } from "../models/filters/groupMountingFilter";
import GroupMountingService from "../services/GroupMountingService";
import { IGroupMountingForm } from "../models/forms/groupMountingForm";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { IMountingPlan } from "../models/mountingPlan";
import { useAttachmentStore } from "./attachment.store";
import GroupMountingViewModel from "../models/viewModels/groupMountingViewModel";
import PrintBoarFilterViewModel from "../models/viewModels/impression/printBoarFilterViewModel";
import EtiquetteViewModel from "../models/viewModels/impression/etiquetteViewModel";
import { useSanitaryProtocolStore } from "./sanitaire/sanitaryProtocol.store";
import { useMountingPlanStore } from "./mountingPlan.store";
import signalRManager from "../helper/signalRManager";
import i18n from "@/i18n";
import IPayloadStore from "../models/api/payloadStore";

export const useGroupMountingStore = defineStore("groupMountings", () => {
    // State
    const datas = ref<IGroupMounting[]>([]);
    const current = ref<IGroupMounting>({} as IGroupMounting);
    const itemForm = ref<IGroupMountingForm>({} as IGroupMountingForm);
    const isLoading = ref<boolean>(false);
    const mountingPlanId = ref<number>(0);
    const printFilter = ref(new PrintBoarFilterViewModel());

    // Actions - Hydration
    /**
     * Hydrate GroupMountings from API response
     */
    async function hydrateGroupMountings(groups: IGroupMountingApiResponse[]): Promise<IGroupMounting[]> {
        if (groups.length === 0) return [];

        // 1. Get stores
        const mountingPlanStore = useMountingPlanStore();

        // 2. Load in parallel if needed
        const loadPromises: Promise<unknown>[] = [];
        if (mountingPlanStore.datas.length === 0) {
            loadPromises.push(mountingPlanStore.load({ page: 1, pageSize: 0 }));
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Create Maps for O(1) lookups
        const mountingPlanMap = new Map(mountingPlanStore.datas.map(m => [m.id, m]));

        // 4. Hydrate all entities in one pass
        const models: IGroupMounting[] = [];
        for (const group of groups) {
            const model = { ...group } as unknown as IGroupMounting;

            // Link references (O(1) via Map)
            model.mountingPlan = mountingPlanMap.get(group.mountingPlanId) ?? model.mountingPlan;

            models.push(model);
        }

        return models;
    }

    // Getters
    const getAll = computed((): GroupMountingViewModel[] => {
        const attachmentStore = useAttachmentStore();
        return datas.value
            .sort((a, b) => a.number - b.number)
            .map((groupMounting) => {
                return {
                    id: groupMounting.id,
                    groupMounting: groupMounting,
                    attachments: attachmentStore.getByGroupMountingId(groupMounting.id)
                } as GroupMountingViewModel;
            });
    });

    const getSelectedBoarsByFilter = computed((): EtiquetteViewModel[] => {
        const attachmentStore = useAttachmentStore();
        const sanitaryProtocolStore = useSanitaryProtocolStore();
        const filtered = attachmentStore.datas.filter((attachment) => attachment.groupMounting?.mountingPlan.id === mountingPlanId.value).sort((a, b) => {
            if (a.groupMounting && b.groupMounting) {
                if (a.groupMounting.number === b.groupMounting.number) {
                    if (a.boar?.geneticType?.code3 && b.boar?.geneticType?.code3) {
                        return a.boar.geneticType.code3.localeCompare(b.boar.geneticType.code3);
                    }
                    return 0;
                }
                return a.groupMounting.number - b.groupMounting.number;
            }
            return 0;
        });

        const grouped = filtered.reduce((acc, attachment) => {
            const groupId = attachment.groupMounting?.id;
            const schemaId = attachment.boar?.geneticType?.id;
            if (groupId && schemaId) {
                if (!acc[groupId]) {
                    acc[groupId] = {};
                }
                if (!acc[groupId][schemaId]) {
                    acc[groupId][schemaId] = [];
                }
                acc[groupId][schemaId].push(attachment);
            }
            return acc;
        }, {} as Record<number, Record<number, typeof filtered>>);

        const result: EtiquetteViewModel[] = [];
        let positionInGroup = 1;
        for (const groupId in grouped) {
            for (const schemaId in grouped[groupId]) {
                result.push({
                    workNumber: grouped[groupId][schemaId][0].boar?.workNumber || "",
                    groupName: "G" + grouped[groupId][schemaId][0].groupMounting?.number || "",
                    groupPosition: positionInGroup,
                    typeProtocolCodes: sanitaryProtocolStore.getTypeProtocolByBoarId(grouped[groupId][schemaId][0].boar?.id || 0).map(tp => tp.code) || [],
                    boarName: grouped[groupId][schemaId][0].boar?.name || "",
                    schemaCode: grouped[groupId][schemaId][0].boar?.geneticType?.code3 || "",
                    isNN: grouped[groupId][schemaId][0].boar?.nn || false,
                    isArmada: grouped[groupId][schemaId][0].boar?.armada || false,
                    barn: grouped[groupId][schemaId][0].boar?.barn || 0,
                    corridor: grouped[groupId][schemaId][0].boar?.corridor || "",
                    box: grouped[groupId][schemaId][0].boar?.box || 0,
                    state: grouped[groupId][schemaId][0].boar?.productionStatus || "",
                    conservationAptitude: grouped[groupId][schemaId][0].boar?.conservationAptitude || ""
                } as EtiquetteViewModel);
                positionInGroup++;
            }
            positionInGroup = 1;
        }
        return result;
    });

    const getAllByMountingPlanId = computed(() => {
        const attachmentStore = useAttachmentStore();
        const mountingPlanStore = useMountingPlanStore();
        // Le bâtiment du plan se lit sur le plan à jour du store des plans : la navigation
        // groupMounting.mountingPlan est figée à l'hydratation et n'a pas de bâtiment si les plans
        // avaient été chargés pendant que le store des bâtiments contenait un autre centre — plus
        // aucun groupe ne s'affichait alors après un changement de centre (suite Mantis 0028108).
        const selectedPlan = mountingPlanStore.datas.find(p => p.id === mountingPlanId.value);
        const planBuildingId = selectedPlan ? selectedPlan.buildingId : undefined;
        return datas.value
            .filter((groupMounting) =>
                groupMounting.mountingPlan?.id === mountingPlanId.value &&
                (planBuildingId ?? groupMounting.mountingPlan?.building?.id) === mountingPlanStore.buildingId
            )
            .sort((a, b) => a.number - b.number)
            .map((groupMounting) => {
                return {
                    id: groupMounting.id,
                    groupMounting: groupMounting,
                    attachments: attachmentStore.getByGroupMountingId(groupMounting.id)
                } as GroupMountingViewModel;
            });
    });

    // Actions
    async function load(filter: IGroupMountingFilter) {
        isLoading.value = true;
        let result = await GroupMountingService.getAll<IGroupMountingFilter, IGroupMountingApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateGroupMountings(result.data as IGroupMountingApiResponse[]);
        }
        return result;
    }

    async function get(id: number) {
        let result = await GroupMountingService.getById<IGroupMountingApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateGroupMountings([result.data as IGroupMountingApiResponse]);

            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = modelData;

            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IGroupMounting | IDataOperationResult<IGroupMounting>>;
        if (itemForm.value.id > 0) {
            result = await GroupMountingService.update(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateGroupMountings([result.data as IGroupMountingApiResponse]);
                current.value = modelData;

                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<IGroupMounting>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await GroupMountingService.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateGroupMountings([result.data as IGroupMountingApiResponse]);
                current.value = modelData;
            }
        }
        return result;
    }

    async function deleteAction() {
        let result = await GroupMountingService.delete<IGroupMountingApiResponse>(current.value.id, true);
        if (!result.error) {
            let index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem();
        }
        return result;
    }

    async function batchDelete(ids: number[]) {
        isLoading.value = true;
        const result = await GroupMountingService.batchDelete(ids);
        if (!result.error) {
            datas.value = datas.value.filter(item => !ids.includes(item.id));
        }
        isLoading.value = false;
        return result;
    }

    function toForm(item: IGroupMounting): IGroupMountingForm {
        const form = {} as IGroupMountingForm;

        form.id = item.id;
        form.name = item.name;
        form.color = item.color;
        form.date = item.date;
        form.number = item.number;
        form.mountingPlanId = item.mountingPlan ? item.mountingPlan.id : 0;
        // Lot9 — sans ce report, la sauvegarde envoie groupTemplateId=null et détache le groupe de son gabarit
        form.groupTemplateId = item.groupTemplateId;

        itemForm.value = form;
        return form;
    }

    function newForm(lienId?: number): IGroupMountingForm {
        itemForm.value = {
            id: 0,
            date: new Date(),
            name: '',
            color: '',
            number: Math.max(...datas.value.filter(d => d.mountingPlan?.id === lienId).map(d => d.number), 0) + 1,
            mountingPlanId: lienId ?? 0,
        } as IGroupMountingForm;
        return itemForm.value;
    }

    function newItem(): IGroupMounting {
        current.value = {
            id: 0,
            date: new Date(),
            name: '',
            color: '',
            number: 0,
            mountingPlan: { id: 0 } as IMountingPlan,
        } as IGroupMounting;

        itemForm.value = toForm(current.value);
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        mountingPlanId,
        printFilter,
        // Getters
        getAll,
        getSelectedBoarsByFilter,
        getAllByMountingPlanId,
        // Actions
        hydrateGroupMountings,
        load,
        get,
        searchStoreById,
        save,
        delete: deleteAction,
        batchDelete,
        toForm,
        newForm,
        newItem
    };
});

signalRManager.connection.on("MajGroupMounting", async (payload: IPayloadStore) => {
    const store = useGroupMountingStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateGroupMountings([payload.data as IGroupMountingApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateGroupMountings([payload.data as IGroupMountingApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
